import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminSidebar from '../components/layout/AdminSidebar';
import { 
  Home, Plus, Edit, Trash2, Eye, EyeOff, Save, X,
  GripVertical, ExternalLink, Loader2, Check, AlertTriangle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../components/ui/dialog';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Sortable Collection Card
const SortableCollectionCard = ({ collection, onEdit, onDelete, onToggle }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: collection.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`bg-white rounded-xl border ${collection.is_active ? 'border-slate-200' : 'border-amber-200 bg-amber-50/50'} p-4 flex items-start gap-4`}
    >
      <div
        {...attributes}
        {...listeners}
        className="cursor-grab active:cursor-grabbing p-1 hover:bg-slate-100 rounded"
      >
        <GripVertical className="w-5 h-5 text-slate-400" />
      </div>
      
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <h3 className="font-semibold text-slate-900">{collection.name}</h3>
          {!collection.is_active && (
            <span className="px-2 py-0.5 text-xs bg-amber-100 text-amber-700 rounded-full">Hidden</span>
          )}
        </div>
        {collection.description && (
          <p className="text-sm text-slate-600 mb-2">{collection.description}</p>
        )}
        <div className="text-xs text-slate-400 font-mono truncate">
          {collection.embed_code?.substring(0, 80)}...
        </div>
      </div>
      
      <div className="flex items-center gap-2">
        <button
          onClick={() => onToggle(collection)}
          className={`p-2 rounded-lg transition-colors ${collection.is_active ? 'text-green-600 hover:bg-green-50' : 'text-slate-400 hover:bg-slate-100'}`}
          title={collection.is_active ? 'Hide collection' : 'Show collection'}
        >
          {collection.is_active ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
        </button>
        <button
          onClick={() => onEdit(collection)}
          className="p-2 text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
          title="Edit collection"
        >
          <Edit className="w-4 h-4" />
        </button>
        <button
          onClick={() => onDelete(collection)}
          className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
          title="Delete collection"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

const AdminPlansPage = () => {
  const navigate = useNavigate();
  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  
  // Navigation visibility
  const [showPlansInNav, setShowPlansInNav] = useState(true);
  const [savingNavSettings, setSavingNavSettings] = useState(false);
  
  // Modal states
  const [collectionModalOpen, setCollectionModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [editingCollection, setEditingCollection] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  
  // Form state
  const [collectionForm, setCollectionForm] = useState({
    name: '',
    description: '',
    embed_code: '',
    is_active: true,
  });

  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  useEffect(() => {
    const isLoggedIn = localStorage.getItem('adminLoggedIn');
    const adminUser = localStorage.getItem('admin_user');
    
    if (isLoggedIn || adminUser) {
      setIsAuthenticated(true);
      fetchCollections();
      fetchNavSettings();
    } else {
      navigate('/admin/login');
    }
  }, [navigate]);

  const fetchNavSettings = async () => {
    try {
      const res = await fetch(`${API_URL}/api/site-settings/navigation`);
      const data = await res.json();
      setShowPlansInNav(data.show_plans !== false);
    } catch (error) {
      console.error('Error fetching nav settings:', error);
    }
  };

  const togglePlansVisibility = async () => {
    setSavingNavSettings(true);
    try {
      // First get current settings to preserve show_standards
      const currentRes = await fetch(`${API_URL}/api/site-settings/navigation`);
      const current = await currentRes.json();
      
      const newValue = !showPlansInNav;
      const res = await fetch(`${API_URL}/api/site-settings/navigation`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          show_standards: current.show_standards !== false,
          show_plans: newValue 
        }),
      });
      if (res.ok) {
        setShowPlansInNav(newValue);
        toast.success(newValue ? 'Plans menu enabled' : 'Plans menu hidden');
      } else {
        toast.error('Failed to update setting');
      }
    } catch (error) {
      console.error('Error updating nav settings:', error);
      toast.error('Failed to update setting');
    } finally {
      setSavingNavSettings(false);
    }
  };

  const fetchCollections = async () => {
    try {
      const res = await fetch(`${API_URL}/api/site-settings/plans/collections`);
      const data = await res.json();
      setCollections(data.collections || []);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching collections:', error);
      toast.error('Failed to load collections');
      setLoading(false);
    }
  };

  const openCollectionModal = (collection = null) => {
    if (collection) {
      setEditingCollection(collection);
      setCollectionForm({
        name: collection.name,
        description: collection.description || '',
        embed_code: collection.embed_code,
        is_active: collection.is_active,
      });
    } else {
      setEditingCollection(null);
      setCollectionForm({
        name: '',
        description: '',
        embed_code: '',
        is_active: true,
      });
    }
    setCollectionModalOpen(true);
  };

  const saveCollection = async () => {
    if (!collectionForm.name || !collectionForm.embed_code) {
      toast.error('Name and embed code are required');
      return;
    }

    setSaving(true);
    try {
      const url = editingCollection
        ? `${API_URL}/api/site-settings/plans/collections/${editingCollection.id}`
        : `${API_URL}/api/site-settings/plans/collections`;
      
      const res = await fetch(url, {
        method: editingCollection ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(collectionForm),
      });

      if (res.ok) {
        toast.success(editingCollection ? 'Collection updated' : 'Collection created');
        setCollectionModalOpen(false);
        fetchCollections();
      } else {
        toast.error('Failed to save collection');
      }
    } catch (error) {
      console.error('Error saving collection:', error);
      toast.error('Failed to save collection');
    } finally {
      setSaving(false);
    }
  };

  const toggleCollectionActive = async (collection) => {
    try {
      const res = await fetch(`${API_URL}/api/site-settings/plans/collections/${collection.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...collection,
          is_active: !collection.is_active,
        }),
      });

      if (res.ok) {
        toast.success(collection.is_active ? 'Collection hidden' : 'Collection shown');
        fetchCollections();
      }
    } catch (error) {
      console.error('Error toggling collection:', error);
      toast.error('Failed to update collection');
    }
  };

  const confirmDelete = (collection) => {
    setDeleteTarget(collection);
    setDeleteModalOpen(true);
  };

  const deleteCollection = async () => {
    if (!deleteTarget) return;

    try {
      const res = await fetch(`${API_URL}/api/site-settings/plans/collections/${deleteTarget.id}`, {
        method: 'DELETE',
      });

      if (res.ok) {
        toast.success('Collection deleted');
        setDeleteModalOpen(false);
        setDeleteTarget(null);
        fetchCollections();
      } else {
        toast.error('Failed to delete collection');
      }
    } catch (error) {
      console.error('Error deleting collection:', error);
      toast.error('Failed to delete collection');
    }
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    
    if (!over || active.id === over.id) return;
    
    const oldIndex = collections.findIndex(c => c.id === active.id);
    const newIndex = collections.findIndex(c => c.id === over.id);
    
    const newOrder = arrayMove(collections, oldIndex, newIndex);
    setCollections(newOrder);
    
    // Save new order to backend
    try {
      await fetch(`${API_URL}/api/site-settings/plans/collections/reorder`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newOrder.map(c => c.id)),
      });
    } catch (error) {
      console.error('Error saving order:', error);
      toast.error('Failed to save order');
      fetchCollections(); // Revert on error
    }
  };

  if (loading) {
    return (
      <AdminSidebar pageTitle="House Plans" pageIcon={Home}>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-cyan-600" />
        </div>
      </AdminSidebar>
    );
  }

  return (
    <AdminSidebar pageTitle="House Plans" pageIcon={Home}>
      {/* Navigation Visibility Toggle */}
      <div className={`mb-6 p-4 rounded-xl border ${showPlansInNav ? 'bg-green-50 border-green-200' : 'bg-amber-50 border-amber-200'}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {showPlansInNav ? (
              <Eye className="w-5 h-5 text-green-600" />
            ) : (
              <EyeOff className="w-5 h-5 text-amber-600" />
            )}
            <div>
              <h3 className="font-semibold text-slate-900">
                Plans Menu: {showPlansInNav ? 'Visible' : 'Hidden'}
              </h3>
              <p className="text-sm text-slate-600">
                {showPlansInNav 
                  ? 'The "Plans" link is visible in the website header and footer.' 
                  : 'The "Plans" link is hidden from the website header and footer.'}
              </p>
            </div>
          </div>
          <Button
            variant={showPlansInNav ? 'outline' : 'default'}
            onClick={togglePlansVisibility}
            disabled={savingNavSettings}
            className="min-w-[120px]"
          >
            {savingNavSettings ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : showPlansInNav ? (
              <>
                <EyeOff className="w-4 h-4 mr-2" />
                Hide Menu
              </>
            ) : (
              <>
                <Eye className="w-4 h-4 mr-2" />
                Show Menu
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Info Card */}
      <div className="bg-gradient-to-r from-cyan-50 to-blue-50 border border-cyan-200 rounded-xl p-4 mb-6">
        <div className="flex items-start gap-3">
          <div className="p-2 bg-cyan-100 rounded-lg">
            <Home className="w-5 h-5 text-cyan-600" />
          </div>
          <div>
            <h3 className="font-semibold text-slate-900 mb-1">Architectural Designs Integration</h3>
            <p className="text-sm text-slate-600 mb-2">
              Create collections of house plans from your Architectural Designs Pro account. 
              Each collection can be embedded on your Plans page.
            </p>
            <a 
              href="https://www.architecturaldesigns.com" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-sm text-cyan-600 hover:underline inline-flex items-center gap-1"
            >
              Open Architectural Designs <ExternalLink className="w-3 h-3" />
            </a>
          </div>
        </div>
      </div>

      {/* Actions Bar */}
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-2">
          <Button onClick={() => openCollectionModal()}>
            <Plus className="w-4 h-4 mr-2" />
            Add Collection
          </Button>
          <Button variant="outline" asChild>
            <a href="/plans" target="_blank" rel="noopener noreferrer">
              <Eye className="w-4 h-4 mr-2" />
              View Plans Page
            </a>
          </Button>
        </div>
        <p className="text-sm text-slate-500">
          {collections.length} collection{collections.length !== 1 ? 's' : ''} • 
          {collections.filter(c => c.is_active).length} active
        </p>
      </div>

      {/* Collections List */}
      {collections.length === 0 ? (
        <div className="bg-white rounded-xl border border-dashed border-slate-300 p-12 text-center">
          <Home className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-slate-900 mb-2">No Collections Yet</h3>
          <p className="text-slate-600 mb-4">
            Create your first collection by adding an embed code from Architectural Designs.
          </p>
          <Button onClick={() => openCollectionModal()}>
            <Plus className="w-4 h-4 mr-2" />
            Add Your First Collection
          </Button>
        </div>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={collections.map(c => c.id)}
            strategy={verticalListSortingStrategy}
          >
            <div className="space-y-3">
              {collections.map((collection) => (
                <SortableCollectionCard
                  key={collection.id}
                  collection={collection}
                  onEdit={openCollectionModal}
                  onDelete={confirmDelete}
                  onToggle={toggleCollectionActive}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}

      {/* How to Get Embed Code */}
      <div className="mt-8 bg-slate-50 rounded-xl p-6 border border-slate-200">
        <h3 className="font-semibold text-slate-900 mb-3">How to Get Your Embed Code</h3>
        <ol className="list-decimal list-inside space-y-2 text-sm text-slate-600">
          <li>Log in to your <a href="https://www.architecturaldesigns.com" target="_blank" rel="noopener noreferrer" className="text-cyan-600 hover:underline">Architectural Designs</a> Pro account</li>
          <li>Go to your Collections and create or select a collection</li>
          <li>Click the &quot;Embed&quot; or &quot;Widget&quot; option for that collection</li>
          <li>Copy the entire embed code (starts with &lt;div or &lt;iframe)</li>
          <li>Paste it here when adding a new collection</li>
        </ol>
      </div>

      {/* Collection Modal */}
      <Dialog open={collectionModalOpen} onOpenChange={setCollectionModalOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {editingCollection ? 'Edit Collection' : 'Add New Collection'}
            </DialogTitle>
            <DialogDescription>
              {editingCollection 
                ? 'Update the collection details below.' 
                : 'Add a new plan collection from Architectural Designs.'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Collection Name *
              </label>
              <Input
                value={collectionForm.name}
                onChange={(e) => setCollectionForm({ ...collectionForm, name: e.target.value })}
                placeholder="e.g., Modern Farmhouse, Barndominiums"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Description
              </label>
              <Textarea
                value={collectionForm.description}
                onChange={(e) => setCollectionForm({ ...collectionForm, description: e.target.value })}
                placeholder="Brief description of this collection style"
                rows={2}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Embed Code *
              </label>
              <Textarea
                value={collectionForm.embed_code}
                onChange={(e) => setCollectionForm({ ...collectionForm, embed_code: e.target.value })}
                placeholder='<div id="Container" style="..."><iframe src="..."></iframe></div>'
                rows={4}
                className="font-mono text-xs"
              />
              <p className="text-xs text-slate-500 mt-1">
                Paste the full embed code from Architectural Designs
              </p>
            </div>

            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
              <div>
                <p className="font-medium text-slate-900">Show on Plans Page</p>
                <p className="text-sm text-slate-500">Make this collection visible to visitors</p>
              </div>
              <Switch
                checked={collectionForm.is_active}
                onCheckedChange={(checked) => setCollectionForm({ ...collectionForm, is_active: checked })}
              />
            </div>
          </div>

          <div className="flex justify-end gap-3 mt-6">
            <Button variant="outline" onClick={() => setCollectionModalOpen(false)}>
              Cancel
            </Button>
            <Button onClick={saveCollection} disabled={saving}>
              {saving ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              {editingCollection ? 'Update' : 'Create'} Collection
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog open={deleteModalOpen} onOpenChange={setDeleteModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" />
              Delete Collection
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to delete &quot;{deleteTarget?.name}&quot;? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-3 mt-4">
            <Button variant="outline" onClick={() => setDeleteModalOpen(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={deleteCollection}>
              <Trash2 className="w-4 h-4 mr-2" />
              Delete Collection
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </AdminSidebar>
  );
};

export default AdminPlansPage;
